/**
 * Browser-side timeout, retry and reconnect settings.
 *
 * The admin timeout control panel publishes its effective `client.*` settings on
 * a public endpoint. Until that answer arrives, the last answer cached in
 * localStorage (or the compiled-in defaults) applies, so a slow or failing
 * endpoint never changes behaviour. The endpoint is read with plain fetch, never
 * through axiosInstance or fetchWithRetry, because both read these settings.
 */

export const CLIENT_TIMEOUTS_PATH = '/api/v1/settings/client-timeouts';

export const CLIENT_TIMEOUTS_STORAGE_KEY = 'client_timeouts_v1';

const SERVER_KEYS = {
  apiTimeoutMs: 'api_timeout_ms',
  apiRetryAttempts: 'api_retry_attempts',
  fetchRetryAttempts: 'fetch_retry_attempts',
  sseHeartbeatTimeoutMs: 'sse_heartbeat_timeout_ms',
  sseMaxRetries: 'sse_max_retries',
  sseBaseDelayMs: 'sse_base_delay_ms',
  planningStallTimeoutMs: 'planning_stall_timeout_ms',
  startDesignSafetyTimeoutMs: 'start_design_safety_timeout_ms',
  sseMaxBackoffMs: 'sse_max_backoff_ms',
  apiRetryBackoffMs: 'api_retry_backoff_ms',
  apiRetryBackoffMaxMs: 'api_retry_backoff_max_ms',
  designPreviewFetchAttempts: 'design_preview_fetch_attempts',
  designPreviewRetryDelayMs: 'design_preview_retry_delay_ms',
  actionInvocationAttempts: 'action_invocation_attempts',
  projectReconnectAttempts: 'project_reconnect_attempts',
  projectReconnectCountdownSeconds: 'project_reconnect_countdown_s',
  planningTasksPollMs: 'planning_tasks_poll_ms',
  designReconcilePollMs: 'design_reconcile_poll_ms',
  planningStallCheckIntervalMs: 'planning_stall_check_interval_ms',
} as const;

export type ClientTimeoutName = keyof typeof SERVER_KEYS;

type ClientTimeoutValues = Partial<Record<ClientTimeoutName, number>>;

/**
 * Compiled-in values used until the panel's answer (or its cached copy) is known.
 * REACT_APP_API_TIMEOUT only seeds that first value: the endpoint always returns every
 * client setting, so the panel's `client.api_timeout_ms` takes precedence once it loads.
 */
export const CLIENT_TIMEOUT_DEFAULTS: Readonly<Record<ClientTimeoutName, number>> = {
  apiTimeoutMs: parseInt(process.env.REACT_APP_API_TIMEOUT || '10000'),
  apiRetryAttempts: 2,
  fetchRetryAttempts: 2,
  sseHeartbeatTimeoutMs: 45_000,
  sseMaxRetries: 5,
  sseBaseDelayMs: 3000,
  planningStallTimeoutMs: 10 * 60 * 1000,
  startDesignSafetyTimeoutMs: 60_000,
  sseMaxBackoffMs: 5 * 60 * 1000,
  apiRetryBackoffMs: 500,
  apiRetryBackoffMaxMs: 1500,
  designPreviewFetchAttempts: 3,
  designPreviewRetryDelayMs: 1000,
  actionInvocationAttempts: 2,
  projectReconnectAttempts: 5,
  projectReconnectCountdownSeconds: 3,
  planningTasksPollMs: 3000,
  designReconcilePollMs: 5000,
  planningStallCheckIntervalMs: 30_000,
};

const NAMES = Object.keys(SERVER_KEYS) as ClientTimeoutName[];

/**
 * How a server value is checked. `retries` may be 0 (no retry) and `attempts` must be at least 1;
 * both are whole numbers. `delay` may be 0 (retry at once); `interval` must be above 0. `seconds`
 * is a delay given in seconds. Every name not listed is an `interval`.
 */
type ValueRule = 'retries' | 'attempts' | 'delay' | 'interval' | 'seconds';

const VALUE_RULES: Partial<Record<ClientTimeoutName, ValueRule>> = {
  apiRetryAttempts: 'retries',
  fetchRetryAttempts: 'retries',
  sseMaxRetries: 'retries',
  projectReconnectAttempts: 'retries',
  designPreviewFetchAttempts: 'attempts',
  actionInvocationAttempts: 'attempts',
  apiRetryBackoffMs: 'delay',
  apiRetryBackoffMaxMs: 'delay',
  designPreviewRetryDelayMs: 'delay',
  projectReconnectCountdownSeconds: 'seconds',
};
const LOAD_TIMEOUT_MS = 5000;
// Timers treat a longer delay as about 1 ms, so such a value is never used.
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;

interface ConfigState {
  values: ClientTimeoutValues;
  hinted: boolean;
  load: Promise<void> | null;
  autoLoad: boolean;
}

// Unit tests stub globalThis.fetch for the code under test; a background load
// would consume those stubs, so tests opt in through the reset helper.
const createState = (autoLoad = process.env.NODE_ENV !== 'test'): ConfigState => ({
  values: {}, hinted: false, load: null, autoLoad,
});

let state = createState();

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const acceptDuration = (raw: number, rule: ValueRule): number | null => {
  const milliseconds = rule === 'seconds' ? raw * 1000 : raw;
  const aboveMinimum = rule === 'interval' ? raw > 0 : raw >= 0;

  return aboveMinimum && milliseconds <= MAX_TIMER_DELAY_MS ? raw : null;
};

const acceptCount = (raw: number, rule: ValueRule): number | null => (
  raw >= (rule === 'attempts' ? 1 : 0) ? Math.floor(raw) : null
);

const acceptValue = (name: ClientTimeoutName, raw: unknown): number | null => {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
  const rule = VALUE_RULES[name] ?? 'interval';

  if (rule === 'retries' || rule === 'attempts') return acceptCount(raw, rule);

  return acceptDuration(raw, rule);
};

/** Keep only the server values that are safe to use; everything else falls back to the default. */
export const sanitizeClientTimeouts = (raw: unknown): ClientTimeoutValues => {
  const source = isRecord(raw) ? raw : {};

  return NAMES.reduce<ClientTimeoutValues>((values, name) => {
    const value = acceptValue(name, source[SERVER_KEYS[name]]);

    return value === null ? values : { ...values, [name]: value };
  }, {});
};

const readCachedValues = (): ClientTimeoutValues => {
  try {
    const stored = localStorage.getItem(CLIENT_TIMEOUTS_STORAGE_KEY);

    return stored ? sanitizeClientTimeouts(JSON.parse(stored)) : {};
  } catch {
    return {};
  }
};

const writeCachedValues = (raw: Record<string, unknown>): void => {
  try {
    localStorage.setItem(CLIENT_TIMEOUTS_STORAGE_KEY, JSON.stringify(raw));
  } catch {
    // Storage may be full or blocked; the live values still apply for this page.
  }
};

const applyCachedHint = (target: ConfigState): void => {
  if (target.hinted) return;
  target.hinted = true;
  target.values = { ...readCachedValues(), ...target.values };
};

const requestServerValues = async (signal?: globalThis.AbortSignal): Promise<unknown> => {
  const url = `${process.env.REACT_APP_API_URL || ''}${CLIENT_TIMEOUTS_PATH}`;
  const response = await globalThis.fetch(url, { headers: { Accept: 'application/json' }, signal });

  if (!response.ok) throw new Error(`Client timeouts request failed with HTTP ${response.status}`);
  const body: unknown = await response.json();

  return isRecord(body) ? body.values : undefined;
};

const requestWithDeadline = async (): Promise<unknown> => {
  const controller = typeof globalThis.AbortController === 'function' ? new globalThis.AbortController() : null;
  const timer = setTimeout(() => controller?.abort(), LOAD_TIMEOUT_MS);

  try {
    return await requestServerValues(controller?.signal);
  } finally {
    clearTimeout(timer);
  }
};

const runLoad = async (target: ConfigState): Promise<void> => {
  try {
    const raw = typeof globalThis.fetch === 'function' ? await requestWithDeadline() : undefined;

    if (!isRecord(raw)) return;
    target.values = sanitizeClientTimeouts(raw);
    writeCachedValues(raw);
  } catch {
    // The endpoint is advisory: keep the cached or compiled-in values.
  }
};

/** Fetch the server values once per page; later calls share the same promise. Never rejects. */
export const loadClientTimeouts = (): Promise<void> => {
  applyCachedHint(state);
  if (!state.load) state.load = runLoad(state);

  return state.load;
};

/** The effective value for `name`: the server value once loaded, else the cached hint, else the default. */
export const getClientTimeout = (name: ClientTimeoutName): number => {
  applyCachedHint(state);
  if (state.autoLoad && !state.load) void loadClientTimeouts();

  return state.values[name] ?? CLIENT_TIMEOUT_DEFAULTS[name];
};

/** Test-only: forget loaded values and the in-flight load. */
export const resetClientTimeoutsForTests = (options: { autoLoad?: boolean } = {}): void => {
  state = createState(options.autoLoad);
};
